import { Button } from '@civilgrid/ui/components';
import { bbox, lineString, point, polygon } from '@turf/turf';
import type { Feature, GeoJsonProperties, LineString, Point, Polygon } from 'geojson';
import { useEffect, useRef, useState } from 'react';
import { useMap } from 'react-map-gl/mapbox';
import { useSnapshot } from 'valtio';
import { captureTypedPosthogEvent, mapObjectTypeToCivildrawObjectType, POSTHOG_EVENTS } from '~/common/posthogEvents';
import type { MapObject } from '~/common/types';
import CloseIconButton from '~/components2/forms/CloseIconButton';
import Input from '~/components2/forms/Input';
import { useClickAwayListener } from '~/hooks/useClickAwayListener';
import rootStore from '~/store';
import layerStore from '~/store2/layers';
import store from '../../../store';
import { Filter } from './Filter';
import { Row } from './Row';

export type FormFieldInput = {
  search: string;
};

export enum SortOrder {
  CreatedAt = 'Created At',
  Title = 'Title',
  Type = 'Type',
}

export default function ObjectsContainer() {
  const { isObjectsVisible } = useSnapshot(layerStore) as typeof layerStore;
  const { hiddenObjects, currentObjectId, currentObject, objectsOnMainSurface, sortOrder, projectId, currentMapId } =
    useSnapshot(store) as typeof store;
  const { mouseEventRef, hasClickedAway, forceClickAwayEvent } = useClickAwayListener();
  const [searchTerm, setSearchTerm] = useState('');
  const sortedAllObjects = useRef<[string, MapObject][]>([]);
  const [sortedAllObjectsState, setSortedAllObjectsState] = useState<[string, MapObject][]>([]);
  const [isDataReady, setIsDataReady] = useState(false);
  const [mouseOverObjectId, setMouseOverObjectId] = useState<string | undefined>();
  const { current: map } = useMap();
  const tooltipSideOffset = 5;
  const defaultExitedObject = { hasExited: false, id: '' };
  const exitedObjectRef = useRef(defaultExitedObject);
  const currentObjectRef = useRef<MapObject>(undefined);
  const [isShowningAllObjects, setIsShowningAllObjects] = useState(true);
  const isInitialLoad = useRef(true);
  const [currentSortOrder, setCurrentSortOrder] = useState<SortOrder | undefined>(undefined);
  const [currentObjectsCount, setCurrentObjectsCount] = useState<number>(0);

  const sortObjects = (supportedObjects: { [key: string]: MapObject }) => {
    const sorted = Object.entries(supportedObjects).sort(([, aObject], [, bObject]) => {
      // Sort objects by...
      switch (sortOrder) {
        // 0-9 then a-z
        case SortOrder.Title: {
          // checks if title is empty and temporarily update it to 'Untitled Object'
          const aTitle = aObject.title === '' ? 'Untitled Object' : aObject.title;
          const bTitle = bObject.title === '' ? 'Untitled Object' : bObject.title;
          return aTitle.localeCompare(bTitle, undefined, {
            numeric: true,
          });
        }
        // a-z
        case SortOrder.Type:
          return aObject.type.localeCompare(bObject.type, undefined, {
            numeric: true,
          });
        default: {
          const a = aObject.createdAt!.toUnixInteger();
          const b = bObject.createdAt!.toUnixInteger();
          if (a > b) {
            return -1;
          }
          return 1;
        }
      }
    });
    sortedAllObjects.current = sorted;
    setSortedAllObjectsState(sorted);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    // Exclude comments and streetViewMarkers as they are not items to include in the list.
    const supportedObjects = Object.entries(objectsOnMainSurface).reduce((iterator, [id, object]) => {
      if (object.type !== 'comment' && object.type !== 'streetViewMarker') {
        iterator = {
          ...iterator,
          [id]: object,
        };
      }
      return iterator;
    }, {});

    if (currentSortOrder !== sortOrder || currentObjectsCount !== Object.keys(supportedObjects).length) {
      try {
        // Gather all objects
        sortObjects(supportedObjects);
      } catch (e) {
        console.error(e);
      } finally {
        setIsDataReady(true);
        setCurrentSortOrder(sortOrder);
        setCurrentObjectsCount(Object.keys(supportedObjects).length);
      }
    }
  }, [sortOrder, objectsOnMainSurface]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    if (isInitialLoad.current) {
      isInitialLoad.current = false;
      return;
    }
    let allIds: string[] = [];
    if (!isShowningAllObjects) {
      allIds = sortedAllObjectsState.map(([id]) => id);
    }
    store.toggleHiddenObjects(allIds);
  }, [isShowningAllObjects]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    if (hasClickedAway && isObjectsVisible) {
      layerStore.toggleObjects();
    }
  }, [hasClickedAway]);

  useEffect(() => {
    if (searchTerm === '') {
      setSortedAllObjectsState(sortedAllObjects.current);
    } else {
      const filteredObject = sortedAllObjects.current.filter(([, object]) =>
        object.title.toLocaleLowerCase().includes(searchTerm.toLocaleLowerCase()),
      );
      setSortedAllObjectsState(filteredObject);
    }
  }, [searchTerm]);

  if (isDataReady === false || sortedAllObjects.current === undefined || !map) return null;

  const onVisibiltyChange = (id: string, objectType: MapObject['type']) => {
    store.toggleHiddenObjects(id);
    captureTypedPosthogEvent(POSTHOG_EVENTS.civildrawObjectVisibilityToggled, {
      project_id: projectId,
      organization_id: rootStore.currentOrganizationId || undefined,
      object_type: mapObjectTypeToCivildrawObjectType(objectType),
      hidden: !hiddenObjects.includes(id), // hiddenObjects won't reflect the toggle until next render
      ...(currentMapId && { map_id: currentMapId }),
    });
  };

  const onGoToLocation = (object: MapObject) => {
    let geoJson: Feature<LineString | Point | Polygon, GeoJsonProperties>;

    switch (object.geometry.type) {
      case 'Point':
        geoJson = point(object.geometry.coordinates);
        break;
      case 'LineString':
        geoJson = lineString(object.geometry.coordinates);
        break;
      default:
        geoJson = polygon(object.geometry.coordinates);
    }

    map.fitBounds(
      bbox(geoJson) as [number, number, number, number],
      {
        padding: 100,
        maxZoom: 20,
        speed: 2,
      },
      map
        .on('movestart', () => {
          document.dispatchEvent(forceClickAwayEvent);
        })
        .on('moveend', () => {
          const { hasExited, id } = exitedObjectRef.current;
          if (hasExited && id === currentObjectRef.current!.id) return;
          store.setCurrentObjectId(object.id);
          exitedObjectRef.current.hasExited = true;
          exitedObjectRef.current.id = object.id;
        }),
    );
    captureTypedPosthogEvent(POSTHOG_EVENTS.civildrawObjectFlownTo, {
      project_id: projectId,
      organization_id: rootStore.currentOrganizationId || undefined,
      object_type: mapObjectTypeToCivildrawObjectType(object.type),
      ...(currentMapId && { map_id: currentMapId }),
    });
  };

  const onButtonClick = () => {
    if (currentObject) {
      store.resetCurrentObjectId();
    }
    layerStore.toggleObjects();
  };

  return (
    <div>
      <Button variant="secondary" onClick={onButtonClick}>
        Objects
      </Button>

      {layerStore.isObjectsVisible && (
        <div ref={mouseEventRef} className="absolute left-0 top-10 h-3/5 w-80" id="objects-menu">
          <div className="space-y-1 rounded-sm bg-white px-4 py-4">
            <div className="flex items-center justify-between">
              <h4>Objects</h4>
              <CloseIconButton onClick={() => layerStore.toggleObjects()} aria-label="Close objects panel" />
            </div>
            <span className="py-2 text-2xs">{sortedAllObjectsState.length.toLocaleString()} Objects</span>
            <Input label="" placeholder="Search..." onChange={setSearchTerm} value={searchTerm} />
            <Filter
              sortOrder={sortOrder}
              isShowningAllObjects={isShowningAllObjects}
              tooltipSideOffset={tooltipSideOffset}
              setIsShowningAllObjects={setIsShowningAllObjects}
            />
          </div>
          <hr className="m-0 w-full border-grey-20 p-0" />
          <div className="max-h-96 overflow-auto rounded-b-lg bg-white">
            {sortedAllObjectsState.map(([id, object], idx) => {
              const isLastObject = idx === sortedAllObjectsState.length - 1;
              const isObjectHidden = hiddenObjects.includes(id);
              return (
                <Row
                  key={id}
                  object={object}
                  isObjectHidden={isObjectHidden}
                  currentObjectId={currentObjectId}
                  currentObjectRef={currentObjectRef}
                  onGoToLocation={onGoToLocation}
                  tooltipSideOffset={tooltipSideOffset}
                  defaultExitedObject={defaultExitedObject}
                  exitedObjectRef={exitedObjectRef}
                  mouseOverObjectId={mouseOverObjectId}
                  onVisibiltyChange={onVisibiltyChange}
                  id={id}
                  isLastObject={isLastObject}
                  setMouseOverObjectId={setMouseOverObjectId}
                />
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
